import { CourseworkRefusal } from "@/components/assignments/CourseworkRefusal";
import { a3Copy } from "@/components/assignments/a3-copy";
import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Bento } from "@/components/ui/box";
import type { Read } from "@/components/ui/box.schema";
import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import { classroomApi, classroomExactQueryKey, type ClassroomCoursework, type ClassroomExactRecord, type ClassroomSubmission } from "@/lib/api/classroom";
import type { ClassroomSubmissionV6, LearningClassroomCourseworkV6, TeachingClassroomCourseworkV6 } from "@/lib/api/courseHome";

import { ClassroomAssignmentRecord, type ClassroomPerspective } from "./ClassroomAssignmentRecord";

/**
 * Opens one stored Classroom record from the retired teaching loop (ADR 0026
 * as amended 2026-10-19, option C). It reads the exact stored record and
 * renders it read-only; nothing here creates, refreshes, connects or sends a
 * person to Classroom.
 */
export interface ClassroomAssignmentSurfaceProps {
  courseId: string;
  perspective: ClassroomPerspective;
  courseworkId: string | null;
  submissionId: string | null;
  timezone: string;
  onSelectDestination: (href: string) => void;
  onTitleChange?: (title: string) => void;
}

function SurfaceRead({ read }: { read: Read<null> }) {
  return <Bento id="classroom-record-read" journey="classroom-history" boxes={[{
    kind: "fact", id: "classroom-record", job: a3Copy.readTheExactStoredClassroomRecord, label: a3Copy.storedRecord,
    owner: { system: "classroom", readAt: null }, read, fold: "never", detail: () => "",
  }]} />;
}

function submissionForRecord(item: ClassroomSubmissionV6): ClassroomSubmission {
  return {
    ...item,
    id: item.provider_submission_id,
    institutional_id: item.institutional_id,
    provider_submission_id: item.provider_submission_id,
    state: item.state,
    assignedGrade: item.assigned_grade ?? undefined,
    draftGrade: item.draft_grade ?? undefined,
    alternateLink: item.provider_url ?? undefined,
    copy_url: item.copy_url,
    provider_url: item.provider_url,
    history: item.history,
    copy_state: item.copy_state,
    last_successful_at: item.last_successful_at,
    unavailable_at: item.unavailable_at,
  };
}

function courseworkForRecord(record: ClassroomExactRecord): ClassroomCoursework {
  const item = record.coursework;
  const teaching = "submissions" in item ? item as TeachingClassroomCourseworkV6 : null;
  const learning = "submission" in item ? item as LearningClassroomCourseworkV6 : null;
  return {
    id: item.provider_coursework_id,
    institutional_id: item.coursework_id,
    provider_coursework_id: item.provider_coursework_id,
    provenance: item.provenance,
    provider_write_eligible: item.provider_write_eligible,
    materials: item.materials?.map(material => ({ link: { title: material.title, url: material.url } })),
    title: item.title,
    description: item.instructions ?? undefined,
    state: item.status,
    due_at: item.due_at,
    alternateLink: item.provider_url ?? undefined,
    maxPoints: item.points ?? undefined,
    last_successful_at: item.last_successful_at,
    unavailable_at: item.unavailable_at,
    posted_read_at: item.posted_read_at ?? null,
    submissions: teaching?.submissions.map(submissionForRecord),
    my_submission: learning?.submission ? submissionForRecord(learning.submission) : null,
  };
}

export function ClassroomAssignmentSurface({ courseId, perspective, courseworkId, submissionId, timezone, onSelectDestination, onTitleChange }: ClassroomAssignmentSurfaceProps) {
  const { me } = useAuth();
  const tenantId = me?.tenant_id ?? "anonymous";
  const userId = me?.user_id ?? "anonymous";
  const exact = useQuery({
    queryKey: classroomExactQueryKey(tenantId, userId, courseId, courseworkId ?? "missing", submissionId),
    queryFn: () => submissionId
      ? classroomApi.getSubmission(courseId, courseworkId!, submissionId)
      : classroomApi.getCoursework(courseId, courseworkId!),
    enabled: Boolean(courseworkId),
    retry: false,
  });
  const status = exact.error instanceof ApiError ? exact.error.status : null;
  const denied = status === 403 || status === 404;
  const work = useMemo(() => exact.data && !denied ? courseworkForRecord(exact.data) : undefined, [exact.data, denied]);
  useEffect(() => { if (denied) onTitleChange?.(""); else if (work) onTitleChange?.(work.title); }, [denied, onTitleChange, work]);
  if (!courseworkId) return null;
  if (denied) return <CourseworkRefusal id="classroom-history-refusal"><p>{a3Copy.thatStoredRecordIsUnavailable}</p>
    <Button data-act="exit" variant="subtle" onClick={() => onSelectDestination("?view=coursework")}>{a3Copy.backToCoursework}</Button></CourseworkRefusal>;
  if (exact.isPending) return <SurfaceRead read={{ status: "loading" }} />;
  if (!exact.data || !work) {
    return <SurfaceRead read={{ status: "failed", sentence: a3Copy.theStoredRecordCouldNotBeRead, retry: () => void exact.refetch() }} />;
  }
  return <ClassroomAssignmentRecord onRefresh={() => void exact.refetch()} work={work} perspective={perspective} selectedSubmissionId={submissionId} onSelectionChange={(_id, href) => { if (href) onSelectDestination(href); }} timezone={timezone} />;
}
